'use client';

import { c as _c } from "react/compiler-runtime";
import { datadogRum } from '@datadog/browser-rum';
import type { AnswerValue } from '@repo/contracts/cardfinder/funnel/types';
import { useSnackbar } from '@repo/ui/Snackbar/useSnackbar';
import { useRouter } from 'next/navigation';
import { type FunnelSessionUpdate, useFunnelSession } from '@/entities/session/model/funnel-session-context';
import type { FunnelSession } from '@/entities/session/model/types';
import { getBranchForPrimaryGoal, getBranchStep, getNextStepFromPrimaryGoal, getPreviousStepId, resolveNextStepId } from '@/shared/config/funnel/branch';
import type { BranchId } from '@/shared/config/funnel/constants';
import { ENTRY_STEP_ID, FINDER_ENTRY_PATH, RECOMMENDATIONS } from '@/shared/config/funnel/flowConstants';
import { getBranchFromAnswers, getBranchSlug, getStepSlug } from '@/shared/config/funnel/utils/client';
import { withCurrentSearchParams, withCurrentSearchParamsExcept } from '@/shared/lib/searchParams';
import { FUNNEL_INTERRUPTS } from '../config/interrupts';
import { resolveBranchEntryBackTo } from '../lib/navigationUtils';
import { useBranchId, useFunnelFlattenedConfig, useFunnelStepId } from './Context/hooks';
import { useFunnelContextChanges } from './in-funnel/context-changes';
import { useFunnelProgression } from './in-funnel/progression';
import { applyInterruptOutcome } from './interrupts/applyOutcome';
import { useFunnelInterrupt } from './interrupts/Context';
import { getDismissedInterruptIds } from './interrupts/dismissed';
import { findMatchingInterrupt } from './interrupts/match';
import { useFinishFunnel } from './useFinishFunnel';
import { useFunnelFormAction } from './useFunnelFormAction';
import { useFunnelPageAction } from './useFunnelPageAction';
export function useFunnelNavigation() {
  const $ = _c(34);
  const funnelFlattenedConfig = useFunnelFlattenedConfig();
  const branchId = useBranchId();
  const stepId = useFunnelStepId();
  const router = useRouter();
  const {
    submit,
    session
  } = useFunnelSession();
  const {
    answerStep,
    recordAnswer
  } = useFunnelProgression();
  const {
    changeGoal
  } = useFunnelContextChanges();
  const finishFunnel = useFinishFunnel();
  const firePageActionAnalyticsEvent = useFunnelPageAction();
  const fireFormActionAnalyticsEvent = useFunnelFormAction();
  const snackbar = useSnackbar();
  const {
    beginInterrupt
  } = useFunnelInterrupt();
  let t0;
  if ($[0] !== funnelFlattenedConfig || $[1] !== router || $[2] !== snackbar) {
    t0 = (targetStepId, currentBranchId) => {
      snackbar.dismiss();
      const branchStep = getBranchStep(funnelFlattenedConfig, currentBranchId, targetStepId);
      if (!branchStep) {
        datadogRum.addError(new Error("Branch step not found"));
        router.push(FINDER_ENTRY_PATH);
      } else {
        router.push(withCurrentSearchParams(`/credit-cards/cardfinder/${getBranchSlug(currentBranchId)}/${getStepSlug(branchStep.step)}`));
      }
    };
    $[0] = funnelFlattenedConfig;
    $[1] = router;
    $[2] = snackbar;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const goToStep = t0;
  let t1;
  if ($[4] !== branchId || $[5] !== funnelFlattenedConfig || $[6] !== router || $[7] !== session || $[8] !== stepId) {
    t1 = () => {
      if (branchId) {
        const previousStepId = getPreviousStepId(funnelFlattenedConfig, branchId, stepId, session.answers);
        if (previousStepId) {
          router.replace(withCurrentSearchParams(`/credit-cards/cardfinder/${getBranchSlug(branchId)}/${getStepSlug({
            id: previousStepId
          })}`));
          return;
        }
        const backTo = resolveBranchEntryBackTo(new URLSearchParams(window.location.search).get("backTo"));
        router.replace(withCurrentSearchParamsExcept(backTo, ["backTo"]));
        return;
      }
      router.back();
    };
    $[4] = branchId;
    $[5] = funnelFlattenedConfig;
    $[6] = router;
    $[7] = session;
    $[8] = stepId;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const goBack = t1;
  let t2;
  if ($[10] !== finishFunnel || $[11] !== goToStep || $[12] !== snackbar) {
    t2 = async (currentStepId, currentBranchId_0, next, isLastStep, session_0, submittedAnswer) => {
      if (isLastStep) {
        snackbar.dismiss();
        await finishFunnel(currentBranchId_0, session_0);
        return;
      }
      if (next === undefined) {
        datadogRum.addError(new Error("Next step was not found"), {
          stepId: currentStepId,
          answer: typeof submittedAnswer === "string" ? submittedAnswer : undefined
        });
        return;
      }
      if (next === currentStepId) {
        datadogRum.addError(new Error("Next step is the same as the current step"));
      }
      goToStep(next, currentBranchId_0);
    };
    $[10] = finishFunnel;
    $[11] = goToStep;
    $[12] = snackbar;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const resumeAfterStep = t2;
  let t3;
  if ($[14] !== answerStep || $[15] !== beginInterrupt || $[16] !== branchId || $[17] !== changeGoal || $[18] !== fireFormActionAnalyticsEvent || $[19] !== firePageActionAnalyticsEvent || $[20] !== funnelFlattenedConfig || $[21] !== goToStep || $[22] !== recordAnswer || $[23] !== resumeAfterStep || $[24] !== session || $[25] !== submit) {
    t3 = async (currentStepId_0, submittedAnswer_0) => {
      const routingAnswer = typeof submittedAnswer_0 === "string" ? submittedAnswer_0 : undefined;
      const branchIdFromAnswers = getBranchFromAnswers(funnelFlattenedConfig, session.answers);
      if (!branchId) {
        if (!routingAnswer) {
          datadogRum.addError(new Error("Primary goal should always have a routing answer (single select)."), {
            stepId: currentStepId_0,
            branchId
          });
          return;
        }
        const nextBranchId = getBranchForPrimaryGoal(funnelFlattenedConfig, routingAnswer);
        const nextStep = getNextStepFromPrimaryGoal(funnelFlattenedConfig, routingAnswer);
        if (nextStep === ENTRY_STEP_ID) {
          datadogRum.addError(new Error("Next step is the entry step"), {
            stepId: currentStepId_0,
            branchId,
            answer: routingAnswer
          });
        }
        if (nextStep === currentStepId_0) {
          datadogRum.addError(new Error("Next step is the same as the current step"));
        }
        if (nextStep && nextBranchId) {
          if (branchIdFromAnswers && branchIdFromAnswers !== nextBranchId) {
            changeGoal(routingAnswer);
          } else {
            if ((await answerStep(currentStepId_0, routingAnswer)).kind === "start-over") {
              return;
            }
          }
          firePageActionAnalyticsEvent(currentStepId_0, nextBranchId, submittedAnswer_0);
          fireFormActionAnalyticsEvent(currentStepId_0, nextBranchId, submittedAnswer_0);
          goToStep(nextStep, nextBranchId);
        } else {
          datadogRum.addError(new Error("Next branch was not found"), {
            stepId: currentStepId_0,
            answer: routingAnswer
          });
        }
        return;
      }
      const next_0 = resolveNextStepId(funnelFlattenedConfig, branchId, currentStepId_0, routingAnswer);
      const isLastStep_0 = next_0 === RECOMMENDATIONS;
      let update = {
        kind: "continue",
        session
      };
      if (submittedAnswer_0 !== undefined) {
        update = isLastStep_0 ? {
          kind: "continue",
          session: recordAnswer(currentStepId_0, submittedAnswer_0)
        } : await answerStep(currentStepId_0, submittedAnswer_0);
      }
      if (update.kind === "start-over") {
        return;
      }
      firePageActionAnalyticsEvent(currentStepId_0, branchId, submittedAnswer_0);
      fireFormActionAnalyticsEvent(currentStepId_0, branchId, submittedAnswer_0);
      const matchingInterrupt = findMatchingInterrupt({
        interrupts: FUNNEL_INTERRUPTS,
        stepId: currentStepId_0,
        branchId,
        submittedAnswer: submittedAnswer_0,
        dismissedIds: getDismissedInterruptIds()
      });
      if (matchingInterrupt) {
        beginInterrupt(matchingInterrupt.id, {
          resume: () => submit(() => resumeAfterStep(currentStepId_0, branchId, next_0, isLastStep_0, update.session, submittedAnswer_0)),
          applyOutcome: outcome => applyInterruptOutcome(outcome, {
            changeGoal,
            goToStep,
            funnelFlattenedConfig
          })
        });
        return;
      }
      await resumeAfterStep(currentStepId_0, branchId, next_0, isLastStep_0, update.session, submittedAnswer_0);
    };
    $[14] = answerStep;
    $[15] = beginInterrupt;
    $[16] = branchId;
    $[17] = changeGoal;
    $[18] = fireFormActionAnalyticsEvent;
    $[19] = firePageActionAnalyticsEvent;
    $[20] = funnelFlattenedConfig;
    $[21] = goToStep;
    $[22] = recordAnswer;
    $[23] = resumeAfterStep;
    $[24] = session;
    $[25] = submit;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  const runAdvance = t3;
  let t4;
  if ($[27] !== runAdvance || $[28] !== submit) {
    t4 = (currentStepId_1, submittedAnswer_1) => {
      submit(() => runAdvance(currentStepId_1, submittedAnswer_1));
    };
    $[27] = runAdvance;
    $[28] = submit;
    $[29] = t4;
  } else {
    t4 = $[29];
  }
  const advance = t4;
  let t5;
  if ($[30] !== advance || $[31] !== goBack || $[32] !== goToStep) {
    t5 = {
      goToStep,
      goBack,
      advance
    };
    $[30] = advance;
    $[31] = goBack;
    $[32] = goToStep;
    $[33] = t5;
  } else {
    t5 = $[33];
  }
  return t5;
}